import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { userApi } from '@/entities/user/@x/subscription';
import { baseApi } from '@/shared/api/baseApi';
import { cacheBaseKeys } from '@/shared/api/cacheTags';
import type { SubscribeRequest } from '@/shared/api/requestTypes';
import type {
  CancelScheduleResult,
  Subscription,
  SubscriptionChangeResult,
  User,
} from '@/shared/api/types';

interface SaveSubscriptionResult {
  paid?: boolean;
  link?: string;
  user?: User;
}

interface UpdateSubscriptionArgs {
  // The plan to switch to
  plan?: { id: number | string } | null;
  // A plan id, used when the user has no subscription yet
  id?: number | string;
  // Charge and switch now instead of at the end of the billing period
  force?: boolean;
  // Where Stripe returns the user after 3-D Secure
  redirect_url?: string | null;
}

export const subscriptionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSubscriptionsList: builder.query<
      Subscription,
      { filter?: string } | void
    >({
      query: (arg) => ({
        url: `/subscription/${arg?.filter || 'active'}/`,
        method: 'GET',
      }),
      providesTags: [cacheBaseKeys.settingsSubscriptions],
    }),

    // Schedules the cancellation of the subscription for the end of the paid period
    cancelSubscription: builder.mutation<
      SubscriptionChangeResult,
      { id?: string } | void
    >({
      query: () => ({
        url: '/payments/subscription/change',
        method: 'POST',
        body: { force: false, plan: null },
      }),
      invalidatesTags: [
        cacheBaseKeys.settingsSubscriptions,
        cacheBaseKeys.user,
      ], // TODO: Update to onQueryStarted if has user in response
    }),

    cancelScheduling: builder.mutation<CancelScheduleResult, void>({
      query: () => ({
        url: '/payments/subscription/change',
        method: 'DELETE',
      }),
      invalidatesTags: [
        cacheBaseKeys.settingsSubscriptions,
        cacheBaseKeys.user,
      ], // TODO: Update to onQueryStarted if has user in response
    }),

    saveSubscription: builder.mutation<
      SaveSubscriptionResult,
      SubscribeRequest
    >({
      query: (data) => ({
        url: '/payments/subscribe',
        method: 'POST',
        body: data,
      }),
      async onQueryStarted(_data, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;

          if (response?.paid) {
            // The session slice updates its flags from the same answer
            if (response.user) {
              dispatch(
                userApi.util.updateQueryData(
                  'getUserInfo',
                  undefined,
                  (draft) => {
                    Object.assign(draft, response.user);
                  },
                ),
              );
            }
          }
        } catch (_error) {}
      },
    }),

    updateSubscription: builder.mutation<
      SubscriptionChangeResult,
      UpdateSubscriptionArgs
    >({
      queryFn: async (data, api, _extraOptions, baseQuery) => {
        const selectUserInfo = userApi.endpoints.getUserInfo.select(undefined);
        const userInfo = selectUserInfo(
          api.getState() as Parameters<typeof selectUserInfo>[0],
        )?.data;
        const requestData = userInfo?.subscription
          ? {
              url: '/payments/subscription/change',
              method: 'POST',
              body: { ...data, plan: data?.plan?.id },
            }
          : {
              url: '/payments/subscribe',
              method: 'POST',
              // The plan goes in `plan`; a bare `{ id }` is rejected by the backend
              body: {
                plan: data?.plan?.id ?? data.id,
                redirect_url: data.redirect_url,
              },
            };

        // RTK Query already binds `api` and `extraOptions` to this baseQuery
        return (await baseQuery(requestData)) as
          | { data: SubscriptionChangeResult }
          | { error: FetchBaseQueryError };
      },
      invalidatesTags: [cacheBaseKeys.user, cacheBaseKeys.settingsPayments],
    }),

    // Cancels whatever is scheduled for the subscription: a plan change or a cancellation
    deleteSubscription: builder.mutation<CancelScheduleResult, void>({
      query: () => ({
        url: '/payments/subscription/change',
        method: 'DELETE',
      }),
      invalidatesTags: [cacheBaseKeys.user],
    }),

    // Schedules the change again: to the given plan, or a cancellation when it is `null`
    undoDeleteSubscription: builder.mutation<
      SubscriptionChangeResult,
      { oldSchedule: string | null }
    >({
      query: ({ oldSchedule }) => ({
        url: '/payments/subscription/change',
        method: 'POST',
        body: { plan: oldSchedule, force: false },
      }),
      invalidatesTags: [cacheBaseKeys.user],
    }),
  }),
});

export const {
  useGetSubscriptionsListQuery,
  useLazyGetSubscriptionsListQuery,
  useCancelSubscriptionMutation,
  useCancelSchedulingMutation,
  useSaveSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useDeleteSubscriptionMutation,
  useUndoDeleteSubscriptionMutation,
} = subscriptionApi;
